import type {
  ProviderConfigDetail,
  ProviderConfigField,
} from '@/types/identity';

/**
 * Pure helpers for the schema-driven org provider-config form. Kept separate
 * from the React component so the save-body logic — the one place real bugs
 * live (secret merge vs non-secret replace) — is unit-testable in isolation.
 */

/**
 * Fields the UI should render. `instance`-scoped fields are reserved for a
 * later per-MCP-server phase (contract §6) — hide them so any that leak into
 * the catalog don't render as broken org-level inputs.
 */
export function visibleFields(
  fields: ProviderConfigField[],
): ProviderConfigField[] {
  return fields.filter((f) => (f.scope ?? 'org') !== 'instance');
}

/**
 * Parse a `string-list` field value (a JSON-encoded array string) into rows.
 * Returns `[]` for empty/absent or malformed input — the server is the
 * authority, so a value we can't parse degrades to "no entries" rather than
 * throwing. Non-string entries are dropped; surrounding whitespace is trimmed.
 */
export function parseList(value: string | undefined): string[] {
  const raw = (value ?? '').trim();
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((e): e is string => typeof e === 'string')
      .map((e) => e.trim());
  } catch {
    return [];
  }
}

/**
 * Seed the editable form state from a loaded config. Non-secret fields pre-fill
 * from `values` (falling back to the schema `default`); secret fields always
 * start blank — their stored value is never returned, and a blank secret on
 * save means "keep what's stored".
 */
export function seedFormValues(
  detail: Pick<ProviderConfigDetail, 'fields' | 'values'>,
): Record<string, string> {
  const seeded: Record<string, string> = {};
  for (const field of visibleFields(detail.fields)) {
    if (field.type === 'secret') {
      seeded[field.key] = '';
    } else if (field.type === 'string-list') {
      // The value is a JSON-array string; normalise empty/absent to '[]' so the
      // control and save path always have a valid array string to work with.
      seeded[field.key] = detail.values[field.key] || field.default || '[]';
    } else {
      seeded[field.key] = detail.values[field.key] ?? field.default ?? '';
    }
  }
  return seeded;
}

/**
 * Build the `values` map for a PUT, honouring the two save rules:
 *   - non-secret fields are sent (trimmed) regardless — they replace wholesale,
 *     so omitting one would clear it;
 *   - a secret field is sent only when the admin actually typed a value; a
 *     blank secret input is omitted so the stored secret is preserved.
 *
 * This is what lets an admin rotate a non-secret field (e.g. clientId) without
 * re-entering the secret the API never returns.
 */
export function buildSaveValues(
  fields: ProviderConfigField[],
  formValues: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const field of visibleFields(fields)) {
    const raw = formValues[field.key] ?? '';
    const trimmed = raw.trim();
    if (field.type === 'secret') {
      if (trimmed.length > 0) out[field.key] = trimmed;
    } else {
      out[field.key] = trimmed;
    }
  }
  return out;
}

/**
 * Client-side field validation — UX sugar that mirrors the server. The server
 * re-validates and its 400 is authoritative, so this only needs to catch the
 * obvious cases before a round-trip. Returns an error string or null.
 *
 * `secretConfigured` lets us treat an already-stored secret as satisfying
 * `required` even though the input is (intentionally) blank.
 */
export function validateField(
  field: ProviderConfigField,
  value: string,
  secretConfigured = false,
): string | null {
  // A `string-list` value is a JSON-array string — validate per entry, not the
  // raw string. `required` means "at least one entry"; `pattern` applies to each.
  if (field.type === 'string-list') {
    const entries = parseList(value);
    if (entries.length === 0) {
      return field.required ? `${field.label} is required.` : null;
    }
    if (field.pattern) {
      let re: RegExp | null = null;
      try {
        re = new RegExp(field.pattern);
      } catch {
        // Malformed catalog pattern — defer to the server.
        return null;
      }
      const bad = entries.find((e) => !re!.test(e));
      if (bad) return `"${bad}" is not a valid entry.`;
    }
    return null;
  }

  const trimmed = (value ?? '').trim();

  if (!trimmed) {
    if (!field.required) return null;
    // A configured secret left blank means "keep stored" — that's valid.
    if (field.type === 'secret' && secretConfigured) return null;
    return `${field.label} is required.`;
  }

  if (field.type === 'url') {
    try {
      new URL(trimmed);
    } catch {
      return `${field.label} must be a valid URL.`;
    }
  }

  if (field.type === 'select') {
    const allowed = (field.options ?? []).map((o) => o.value);
    if (!allowed.includes(trimmed)) {
      return `${field.label} must be one of the listed options.`;
    }
  }

  if (field.pattern) {
    try {
      if (!new RegExp(field.pattern).test(trimmed)) {
        return `${field.label} is not in the expected format.`;
      }
    } catch {
      // A malformed pattern from the catalog shouldn't block the user — let
      // the server be the authority.
      return null;
    }
  }

  return null;
}

/**
 * Validate the whole form. Returns a map of field key -> error message for
 * every field that fails; an empty object means the form is submittable.
 */
export function validateConfig(
  fields: ProviderConfigField[],
  formValues: Record<string, string>,
  secrets: Record<string, { configured: boolean }>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of visibleFields(fields)) {
    const err = validateField(
      field,
      formValues[field.key] ?? '',
      secrets[field.key]?.configured ?? false,
    );
    if (err) errors[field.key] = err;
  }
  return errors;
}
